import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, BookOpen, Quote, Target, Heart, Printer, Copy, Check, Facebook, Twitter, Mail, ChevronDown, Sparkles, Compass, Lightbulb, Flame, Shield, Eye, Zap, Sun, Star, Crown, MessageCircle, Image } from 'lucide-react';
import { QuoteSocialImageModal } from './QuoteSocialImageModal';
import ReactPlayer from 'react-player/youtube';
import { supabase, supabaseUrl } from '../lib/supabase';
import { getThemeColors, type StudyPageThemeId, type StudyPageColors, type CustomColorConfig } from '../lib/studyPageThemes';
import { parseISO } from 'date-fns';

interface SharedStudyPageData {
  id: string;
  share_id: string;
  title: string;
  scripture: string | null;
  sermon_date: string | null;
  bulletin_summary: string | null;
  quotes: string[] | null;
  main_points: string[] | null;
  key_scriptures: string[] | null;
  practical_applications: string[] | null;
  prayer_points: string[] | null;
  youtube_url: string | null;
  discussion_questions: string[] | null;
  closing_reflection: string | null;
  theme?: string | null;
  custom_theme_colors?: CustomColorConfig | null;
  created_at: string;
  user_id: string | null;
}

const pointIcons = [Compass, Lightbulb, Heart, Shield, Eye, Zap, Sun, Star, Crown, Flame, Sparkles, Target];

function getPointIcon(index: number) {
  const Icon = pointIcons[index % pointIcons.length];
  return <Icon className="h-5 w-5" />;
}

function buildThemeCSS(t: StudyPageColors): string {
  return `
    .study-page {
      --sp-bg: ${t.background};
      --sp-card-bg: ${t.cardBackground};
      --sp-card-border: ${t.cardBorder};
      --sp-card-hover-bg: ${t.cardHoverBackground};
      --sp-card-hover-border: ${t.cardHoverBorder};
      --sp-text-primary: ${t.textPrimary};
      --sp-text-secondary: ${t.textSecondary};
      --sp-text-muted: ${t.textMuted};
      --sp-accent: ${t.accent};
      --sp-accent-muted: ${t.accentMuted};
      --sp-accent-subtle: ${t.accentSubtle};
      --sp-btn-bg: ${t.buttonBackground};
      --sp-btn-hover: ${t.buttonHover};
      --sp-btn-text: ${t.buttonText};
      --sp-divider: ${t.divider};
      --sp-footer: ${t.footerText};
      --sp-header-bg: ${t.headerBarBg};
    }
  `;
}

export function SharedStudyPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [studyPage, setStudyPage] = useState<SharedStudyPageData | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [quoteCopied, setQuoteCopied] = useState<number | null>(null);
  const [socialPostQuote, setSocialPostQuote] = useState<string | null>(null);
  const studyContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadStudyPage = async () => {
      if (!shareId) {
        setError('Invalid study page link');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('shared_study_pages')
          .select('*')
          .eq('share_id', shareId)
          .single();

        if (fetchError) throw fetchError;
        if (!data) throw new Error('Study page not found');

        setStudyPage(data);

        await supabase
          .from('shared_study_pages')
          .update({ view_count: (data.view_count || 0) + 1 })
          .eq('share_id', shareId);
      } catch (err) {
        console.error('Error loading study page:', err);
        setError('This study page could not be found or has expired.');
      } finally {
        setLoading(false);
      }
    };

    loadStudyPage();
  }, [shareId]);

  const theme = useMemo<StudyPageColors>(() => {
    if (!studyPage) return getThemeColors('dark');
    const themeId = (studyPage.theme || 'dark') as StudyPageThemeId;
    return getThemeColors(themeId, studyPage.custom_theme_colors);
  }, [studyPage]);

  const handleCopyLink = async () => {
    try {
      const socialUrl = supabaseUrl && shareId 
        ? `${supabaseUrl}/functions/v1/study-page-meta?id=${shareId}`
        : window.location.href;
      await navigator.clipboard.writeText(socialUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCopyQuote = async (quote: string, index: number) => {
    try {
      await navigator.clipboard.writeText(quote);
      setQuoteCopied(index);
      setTimeout(() => setQuoteCopied(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = quote;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setQuoteCopied(index);
      setTimeout(() => setQuoteCopied(null), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const scrollToContent = () => {
    studyContentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getSocialShareUrl = () => {
    if (supabaseUrl && shareId) {
      return `${supabaseUrl}/functions/v1/study-page-meta?id=${shareId}`;
    }
    return window.location.href;
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(getSocialShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(getSocialShareUrl());
    const text = encodeURIComponent(`Check out this sermon study: ${studyPage?.title}`);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Sermon Study: ${studyPage?.title}`);
    const body = encodeURIComponent(`I wanted to share this sermon study with you:\n\n${studyPage?.title}\n${studyPage?.scripture || ''}\n\nView it here: ${getSocialShareUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (error || !studyPage) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-neutral-100 mb-2">Study Page Not Found</h1>
          <p className="text-neutral-400 mb-6">{error}</p>
          <Link 
            to="/" 
            className="inline-flex items-center px-4 py-2 bg-neutral-700 text-neutral-100 rounded-lg hover:bg-neutral-600 transition-colors font-medium"
          >
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const formattedDate = studyPage.sermon_date 
    ? parseISO(studyPage.sermon_date).toLocaleDateString('en-US', {
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : null;

  const titleParts = studyPage.title.split(':');
  const hasSubtitle = titleParts.length > 1;

  const getEvenGridClass = () => {
    return 'grid-cols-1 sm:grid-cols-2';
  };

  const t = theme;

  const metaTitle = `${studyPage.title} — Sermon Study Guide`;
  const metaDescription = studyPage.bulletin_summary
    ? studyPage.bulletin_summary.substring(0, 155)
    : `Sermon study guide: ${studyPage.title}${studyPage.scripture ? ` — ${studyPage.scripture}` : ''}.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&family=Inter:wght@300;400;500;600&display=swap');
        
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .hero-section { min-height: auto !important; padding: 2rem 0 !important; }
        }

        .study-page { font-family: 'Inter', sans-serif; }
        .study-page .serif { font-family: 'Playfair Display', serif; }
        
        ${buildThemeCSS(t)}

        .bento-card {
          background: var(--sp-card-bg);
          border: 1px solid var(--sp-card-border);
          border-radius: 16px;
          transition: background 0.3s ease, border-color 0.3s ease;
        }
        
        .bento-card:hover {
          background: var(--sp-card-hover-bg);
          border-color: var(--sp-card-hover-border);
        }

        .icon-badge {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: var(--sp-accent-subtle);
          border: 1px solid var(--sp-card-border);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--sp-accent-muted);
        }

        .scripture-item {
          padding: 12px 0;
          border-bottom: 1px solid var(--sp-divider);
        }
        .scripture-item:last-child {
          border-bottom: none;
        }

        .section-label {
          font-family: 'Inter', sans-serif;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: var(--sp-accent);
        }

        .featured-quote {
          border-left: 2px solid var(--sp-card-hover-border);
          padding-left: 1.25rem;
          font-family: 'Playfair Display', serif;
          font-style: italic;
          color: var(--sp-text-secondary);
        }

        .scroll-cta {
          animation: gentle-bounce 3s ease-in-out infinite;
        }
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }

        .prayer-bullet {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--sp-accent);
          flex-shrink: 0;
          margin-top: 8px;
        }
      `}</style>
      
      <div className="study-page min-h-screen" style={{ backgroundColor: t.background, color: t.textSecondary }}>

        <div className="no-print fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b" style={{ backgroundColor: t.headerBarBg, borderColor: t.divider }}>
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 opacity-60">
              <BookOpen className="h-4 w-4" style={{ color: t.accent }} />
              <span className="text-xs tracking-widest uppercase" style={{ color: t.accent }}>Study Guide</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleCopyLink} className="p-2 rounded-lg transition-colors" style={{ color: t.accent }} title="Copy link">
                {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
              <button onClick={shareOnFacebook} className="p-2 rounded-lg transition-colors" style={{ color: t.accent }} title="Share on Facebook">
                <Facebook className="h-4 w-4" />
              </button>
              <button onClick={shareOnTwitter} className="p-2 rounded-lg transition-colors" style={{ color: t.accent }} title="Share on Twitter">
                <Twitter className="h-4 w-4" />
              </button>
              <button onClick={shareViaEmail} className="p-2 rounded-lg transition-colors" style={{ color: t.accent }} title="Share via Email">
                <Mail className="h-4 w-4" />
              </button>
              <button onClick={handlePrint} className="p-2 rounded-lg transition-colors" style={{ color: t.accent }} title="Print / Save as PDF">
                <Printer className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <section className="hero-section min-h-screen flex flex-col items-center justify-center text-center px-6 relative">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full opacity-[0.03]" style={{ background: `radial-gradient(circle, ${t.accent}, transparent)` }} />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.02]" style={{ background: `radial-gradient(circle, ${t.accent}, transparent)` }} />
          </div>

          <div className="relative z-10 max-w-4xl">
            <div className="inline-block mb-10">
              <span className="section-label px-4 py-2 rounded-full" style={{ border: `1px solid ${t.cardBorder}`, background: t.accentSubtle }}>
                Sermon Study Guide
              </span>
            </div>

            <h1 className="serif mb-8">
              {hasSubtitle ? (
                <>
                  <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.1] mb-2" style={{ color: t.textPrimary }}>
                    {titleParts[0].trim()}:
                  </span>
                  <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold italic leading-[1.1]" style={{ color: t.accentMuted }}>
                    {titleParts.slice(1).join(':').trim()}
                  </span>
                </>
              ) : (
                <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.1]" style={{ color: t.textPrimary }}>
                  {studyPage.title}
                </span>
              )}
            </h1>

            {studyPage.bulletin_summary && (
              <p className="text-lg sm:text-xl font-light leading-relaxed max-w-2xl mx-auto mb-10" style={{ color: t.textMuted }}>
                {studyPage.bulletin_summary.length > 200
                  ? studyPage.bulletin_summary.substring(0, 200).trim() + '...'
                  : studyPage.bulletin_summary}
              </p>
            )}

            <div className="flex items-center justify-center gap-6 flex-wrap">
              <button
                onClick={scrollToContent}
                className="px-8 py-3.5 rounded-xl font-medium transition-colors text-base serif"
                style={{ backgroundColor: t.buttonBackground, color: t.buttonText }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = t.buttonHover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = t.buttonBackground)}
              >
                Explore Study Material
              </button>
              <div className="flex items-center gap-3" style={{ color: t.accent }}>
                {studyPage.scripture && (
                  <span className="text-sm font-light">{studyPage.scripture}</span>
                )}
                {studyPage.scripture && formattedDate && (
                  <span style={{ color: t.divider }}>·</span>
                )}
                {formattedDate && (
                  <span className="text-sm font-light">{formattedDate}</span>
                )}
              </div>
            </div>
          </div>

          <div className="absolute bottom-10 scroll-cta">
            <ChevronDown className="h-6 w-6" style={{ color: t.accent, opacity: 0.4 }} />
          </div>
        </section>

        {studyPage.youtube_url && (
          <section className="px-6 pb-16">
            <div className="max-w-5xl mx-auto">
              <div className="bento-card overflow-hidden">
                <div className="relative pt-[56.25%] bg-black/40">
                  <ReactPlayer
                    url={studyPage.youtube_url}
                    width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                    controls
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        <div ref={studyContentRef} />

        {(studyPage.bulletin_summary || (studyPage.main_points && studyPage.main_points.length > 0)) && (
          <section className="px-6 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
                {studyPage.bulletin_summary && (
                  <div className="lg:col-span-2">
                    <h2 className="serif text-3xl sm:text-4xl font-bold mb-2" style={{ color: t.textPrimary }}>The Core Message</h2>
                    <div className="w-12 h-0.5 mb-8" style={{ backgroundColor: t.accent, opacity: 0.4 }} />
                    <p className="leading-relaxed font-light text-lg mb-8" style={{ color: t.textMuted }}>
                      {studyPage.bulletin_summary}
                    </p>
                    {studyPage.quotes && studyPage.quotes.length > 0 && (
                      <div className="featured-quote">
                        <p className="text-base leading-relaxed">
                          "{studyPage.quotes[0]}"
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {studyPage.main_points && studyPage.main_points.length > 0 && (
                  <div className={studyPage.bulletin_summary ? 'lg:col-span-3' : 'lg:col-span-5'}>
                    <div className={`grid ${getEvenGridClass()} gap-4`}>
                      {studyPage.main_points.map((point, index) => (
                        <div key={index} className="bento-card p-6">
                          <div className="icon-badge mb-4">
                            {getPointIcon(index)}
                          </div>
                          <p className="text-base leading-relaxed font-light" style={{ color: t.textSecondary }}>{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {((studyPage.quotes && studyPage.quotes.length > 1) || false) && (
          <section className="px-6 pb-16">
            <div className="max-w-7xl mx-auto">
              <span className="section-label block mb-8">Shareable Quotes</span>
              <div className={`grid ${getEvenGridClass()} gap-4`}>
                {studyPage.quotes!.slice(1).map((quote, index) => {
                  const remainingQuotes = studyPage.quotes!.slice(1);
                  const isLast = index === remainingQuotes.length - 1;
                  const isOddCount = remainingQuotes.length % 2 !== 0;
                  return (
                    <div key={index} className={`bento-card p-6 ${isLast && isOddCount ? 'sm:col-span-2' : ''}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <Quote className="h-5 w-5 flex-shrink-0" style={{ color: t.accent, opacity: 0.3 }} />
                        <div className="no-print flex items-center gap-1">
                          <button
                            onClick={() => setSocialPostQuote(quote)}
                            className="flex-shrink-0 p-1.5 rounded-md transition-colors hover:opacity-80"
                            style={{ color: t.textMuted }}
                            title="Create social image"
                          >
                            <Image className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleCopyQuote(quote, index)}
                            className="flex-shrink-0 p-1.5 rounded-md transition-colors hover:opacity-80"
                            style={{ color: t.textMuted }}
                            title="Copy quote"
                          >
                            {quoteCopied === index ? (
                              <Check className="h-4 w-4" style={{ color: t.accent }} />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <p className="serif italic text-lg leading-relaxed" style={{ color: t.textSecondary }}>"{quote}"</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {((studyPage.key_scriptures && studyPage.key_scriptures.length > 0) || (studyPage.practical_applications && studyPage.practical_applications.length > 0)) && (
          <section className="px-6 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto">
              <h2 className="serif text-4xl sm:text-5xl font-bold text-center mb-4" style={{ color: t.textPrimary }}>Study Guide</h2>
              <p className="text-center font-light mb-14 max-w-lg mx-auto" style={{ color: t.accent }}>
                Deepen your understanding with these guided reflections and scripture connections.
              </p>

              <div className="grid lg:grid-cols-5 gap-6 mb-14">
                {studyPage.key_scriptures && studyPage.key_scriptures.length > 0 && (
                  <div className={`bento-card p-8 ${studyPage.practical_applications && studyPage.practical_applications.length > 0 ? 'lg:col-span-2' : 'lg:col-span-5'}`}>
                    <span className="section-label">Scripture References</span>
                    <div className="mt-6">
                      {studyPage.key_scriptures.map((scripture, index) => (
                        <div key={index} className="scripture-item">
                          <span className="font-light text-base" style={{ color: t.textSecondary }}>{scripture}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {studyPage.practical_applications && studyPage.practical_applications.length > 0 && (
                  <div className={`bento-card p-8 ${studyPage.key_scriptures && studyPage.key_scriptures.length > 0 ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
                    <span className="section-label">Essential Takeaways</span>
                    <div className={`mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6`}>
                      {studyPage.practical_applications.map((app, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <span className="serif font-bold text-lg mt-0.5" style={{ color: t.accent, opacity: 0.6 }}>{index + 1}</span>
                          <p className="text-base font-light leading-relaxed" style={{ color: t.textMuted }}>{app}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {studyPage.discussion_questions && studyPage.discussion_questions.length > 0 && (
          <section className="px-6 pb-16 sm:pb-24">
            <div className="max-w-7xl mx-auto">
              <span className="section-label block mb-8">Discussion Questions</span>
              <div className={`grid ${getEvenGridClass()} gap-4`}>
                {studyPage.discussion_questions.map((question, index) => (
                  <div key={index} className="bento-card p-6">
                    <div className="flex items-start gap-3">
                      <MessageCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: t.accent, opacity: 0.4 }} />
                      <p className="text-base leading-relaxed font-light" style={{ color: t.textSecondary }}>{question}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="px-6 py-20 sm:py-28">
          <div className="max-w-5xl mx-auto">
            <div className="bento-card p-10 sm:p-16 text-center" style={{ background: t.accentSubtle, borderColor: t.cardBorder }}>
              <h2 className="serif text-3xl sm:text-4xl lg:text-5xl font-bold italic mb-8" style={{ color: t.textSecondary }}>Closing Reflection</h2>
              {studyPage.closing_reflection ? (
                <p className="serif text-xl sm:text-2xl lg:text-3xl font-bold tracking-wide leading-snug max-w-2xl mx-auto mb-8" style={{ color: t.textPrimary }}>
                  {studyPage.closing_reflection}
                </p>
              ) : studyPage.quotes && studyPage.quotes.length > 0 ? (
                <p className="serif text-xl sm:text-2xl lg:text-3xl font-bold tracking-wide leading-snug max-w-2xl mx-auto mb-8" style={{ color: t.textPrimary }}>
                  "{studyPage.quotes[studyPage.quotes.length - 1]}"
                </p>
              ) : null}

              {studyPage.scripture && (
                <p className="text-base tracking-wider mt-4" style={{ color: t.accent, opacity: 0.6 }}>{studyPage.scripture}</p>
              )}
            </div>
          </div>
        </section>

        <footer className="no-print py-8 px-6 text-center" style={{ borderTop: `1px solid ${t.divider}` }}>
          {studyPage.user_id === null ? (
            <div className="max-w-4xl mx-auto">
              <p className="text-sm font-medium mb-3" style={{ color: t.textPrimary }}>
                Powered by <a href="https://sermonbuild.com" className="transition-colors hover:opacity-80 font-semibold" style={{ color: t.accent }}>SermonBuild</a>
              </p>
              <p className="text-xs" style={{ color: t.textMuted }}>
                Create your own professional study guides —{' '}
                <a href="https://sermonbuild.com" className="transition-colors hover:opacity-80 font-medium" style={{ color: t.accent }}>
                  Start your free trial today
                </a>
              </p>
            </div>
          ) : (
            <p className="text-xs tracking-wider" style={{ color: t.footerText }}>
              Created with <a href="https://sermonbuild.com" className="transition-colors hover:opacity-80" style={{ color: t.accent, opacity: 0.5 }}>SermonBuild</a>
            </p>
          )}
        </footer>
      </div>

      {socialPostQuote && (
        <QuoteSocialImageModal
          quote={socialPostQuote}
          sermonTitle={studyPage?.title}
          onClose={() => setSocialPostQuote(null)}
        />
      )}
    </>
  );
}
